import { useDialog } from '@globals/dialog/useDialogStore'
import { colors } from '@globals/themes/defaultTheme'
import { tabletQuery } from '@globals/themes/themeConstants'
import Close from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { t } from '@transifex/native'
import { ReactNode } from 'react'
import BottomDrawer from './BottomDrawer'

export const DRAWER_WIDTH = 375

const drawerStyle: SxProps = {
  '& .MuiDrawer-paper ': {
    maxHeight: '90%',
  },
}

const titleStyle: SxProps = {
  mt: 0,
  [tabletQuery]: {
    mt: 0,
  },
}

const paperStyle: SxProps = {
  width: DRAWER_WIDTH,
  borderRadius: 0,
  backgroundColor: colors.whiteDark,
}

type RightDrawerProps = {
  dialogKey: string
  title: ReactNode
  actionButtonCount?: 0 | 1 | 2
  children: ReactNode
}

const DialogCloseButton = ({ onClose }: { onClose: () => void }) => {
  return (
    <Button aria-label={t('Close dialog button')} id="rightDrawer-closeBtn" onClick={onClose}>
      <Close />
    </Button>
  )
}

const RightDrawer: React.FC<RightDrawerProps> = ({
  dialogKey,
  title,
  actionButtonCount = 0,
  children,
}) => {
  const isTablet = useMediaQuery(tabletQuery)
  const { closeDialog, isOpen } = useDialog(dialogKey)

  if (isTablet)
    return (
      <BottomDrawer
        drawerKey={dialogKey}
        title={title}
        onClose={closeDialog}
        drawerStyles={drawerStyle}
        actionButtonCount={actionButtonCount}>
        {children}
      </BottomDrawer>
    )

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={isOpen}
      onClose={closeDialog}
      closeAfterTransition
      slotProps={{
        paper: {
          sx: {
            ...paperStyle,
          },
        },
        backdrop: {
          'aria-hidden': undefined,
        },
      }}
      ModalProps={{
        keepMounted: true,
        disablePortal: false,
        disableScrollLock: true,
      }}>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          backgroundColor: colors.white,
        }}>
        <Typography variant="h3" sx={titleStyle}>
          {title}
        </Typography>
        <DialogCloseButton onClose={closeDialog} />
      </Stack>
      <Divider />
      <Box
        sx={{
          p: 2,
          mb: actionButtonCount === 1 ? 28 : 2,
        }}>
        {children}
      </Box>
    </Drawer>
  )
}

export default RightDrawer
